import React, { useMemo } from 'react';
import { ThemeProvider } from 'styled-components';
import { Typography } from 'antd';
import { useTheme } from '../../../context/ThemeContext';
import Table from '../../../commonComponents/Table';
import {
  StyledCard,
  TableWrapper,
  EmptyStateText,
} from './Styles';
import { addCommasInPrices, CATEGORY_TABLE_COLUMNS, EMPTY_STATE_MESSAGES } from './constants';
import { CategorySalesSectionProps } from './types';

const { Title } = Typography;

const CategorySalesSection: React.FC<CategorySalesSectionProps> = ({
  reportData,
  loading = false,
}) => {
  const { isDarkMode } = useTheme();
  const theme = { mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark' };

  const tableData = useMemo(() => {
    if (!reportData?.categorySales?.categories || reportData.categorySales.categories.length === 0) {
      return [];
    }

    return reportData.categorySales.categories.map((category, index) => ({
      key: `category-${index}`,
      name: category.name || 'Unknown',
      revenue: `$${addCommasInPrices(category.revenue || 0)}`,
      itemsSold: category.itemsSold || 0,
      orders: category.orders || 0,
    }));
  }, [reportData?.categorySales]);

  if (tableData.length === 0) {
    return (
      <ThemeProvider theme={theme}>
        <StyledCard title={<Title level={4} style={{ margin: 0 }}>Category Sales</Title>}>
          <EmptyStateText>{EMPTY_STATE_MESSAGES.CATEGORY_SALES}</EmptyStateText>
        </StyledCard>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <StyledCard title={<Title level={4} style={{ margin: 0 }}>Category Sales</Title>}>
        <TableWrapper>
          <Table
            columns={CATEGORY_TABLE_COLUMNS}
            dataSource={tableData}
            rowKey={(record) => record.key}
            loading={loading}
            pagination={false}
            emptyText="No category sales data available"
            emptyDescription={EMPTY_STATE_MESSAGES.CATEGORY_SALES}
            scroll={{ x: true, y: 400 }}
            theme={isDarkMode ? 'dark' : 'light'}
            size="middle"
            headerBackgroundColor={isDarkMode ? '#1a202c' : '#F2F2F2'}
            removeVerticalBorders={true}
            thPadding="15px 10px"
            tdPadding="10px 10px"
            firstChildBorderRadius="8px"
            border="0"
            borderBottomLeftRight={true}
          />
        </TableWrapper>
      </StyledCard>
    </ThemeProvider>
  );
};

export default CategorySalesSection;
